import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Search,
  Server,
} from 'lucide-react';
import { customAdminApi } from '../../../services/api';
import { useCustomInstances, useCustomInstanceSocketInvalidation } from '../../../hooks/useCustomInstances';
import { customErrorMessage, formatBytes } from '../../../utils/customUi';
import {
  CUSTOM_INSTANCE_SORTS,
  CUSTOM_INSTANCE_STATES,
  customInstanceListSearch,
  readCustomInstanceListParams,
} from '../../../utils/customInstanceSearch';
import { CustomInstanceActions, CustomInstanceStatus } from './CustomInstanceActions';

interface CustomInstancesIndexProps {
  fixedProductId?: string;
  embedded?: boolean;
}

const LIST_PARAM_KEYS = new Set([
  'q', 'productId', 'state', 'sort', 'dir', 'page', 'pageSize', 'includeDeleted',
]);

export function CustomInstancesIndex({ fixedProductId, embedded = false }: CustomInstancesIndexProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [notice, setNotice] = useState('');
  const params = useMemo(
    () => readCustomInstanceListParams(searchParams, fixedProductId),
    [fixedProductId, searchParams],
  );
  const instancesQuery = useCustomInstances(params);
  useCustomInstanceSocketInvalidation();

  const productsQuery = useQuery({
    queryKey: ['custom-products', 'instance-filter'],
    queryFn: async () => (await customAdminApi.listProducts()).products,
    enabled: !fixedProductId,
    staleTime: 30_000,
  });

  const updateParams = (updates: Record<string, string | number | boolean | undefined>) => {
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(updates)) {
        if (value === undefined || value === '' || value === false) next.delete(key);
        else next.set(key, value === true ? '1' : String(value));
      }
      return next;
    }, { replace: true });
  };

  const resetFilters = () => {
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      for (const key of LIST_PARAM_KEYS) {
        if (key !== 'productId' || !fixedProductId) next.delete(key);
      }
      if (fixedProductId) next.set('productId', fixedProductId);
      return next;
    }, { replace: true });
  };

  const response = instancesQuery.data;
  const instances = response?.instances || [];
  const pagination = response?.pagination;
  const detailSearch = customInstanceListSearch(params);
  const totalPages = Math.max(1, pagination?.totalPages || 1);

  useEffect(() => {
    if (!pagination || params.page <= totalPages) return;
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      if (totalPages === 1) next.delete('page');
      else next.set('page', String(totalPages));
      return next;
    }, { replace: true });
  }, [pagination, params.page, setSearchParams, totalPages]);

  return <div className="space-y-5">
    {!embedded && <div className="flex flex-wrap justify-between items-start gap-3">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Custom Instances</h1>
        <p className="text-sm text-gray-500 mt-1">Compact admin inventory. Launch intent and Docker observations remain separate.</p>
      </div>
      <button className="btn-secondary" onClick={() => void instancesQuery.refetch()}>
        <RefreshCw className={`w-4 h-4 ${instancesQuery.isFetching ? 'animate-spin' : ''}`} /> Refresh
      </button>
    </div>}

    <div className="card p-4">
      <div className="grid sm:grid-cols-2 xl:grid-cols-[minmax(12rem,1fr)_minmax(11rem,0.8fr)_10rem_10rem_8rem_auto] gap-3 items-end">
        <label className="text-xs text-gray-500">Search
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" />
            <input
              className="input pl-9"
              value={params.q || ''}
              maxLength={128}
              placeholder="Name, container or ID"
              onChange={event => updateParams({ q: event.target.value, page: undefined })}
            />
          </div>
        </label>
        {!fixedProductId && <label className="text-xs text-gray-500">Product
          <select
            className="input mt-1"
            value={params.productId || ''}
            onChange={event => updateParams({ productId: event.target.value, page: undefined })}
          >
            <option value="">All products</option>
            {(productsQuery.data || []).map(product => <option key={product.id} value={product.id}>{product.displayName}</option>)}
          </select>
        </label>}
        <label className="text-xs text-gray-500">Actual state
          <select
            className="input mt-1"
            value={params.state || ''}
            onChange={event => updateParams({ state: event.target.value, page: undefined })}
          >
            <option value="">All states</option>
            {CUSTOM_INSTANCE_STATES.map(state => <option key={state} value={state}>{state}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-500">Sort
          <select
            className="input mt-1"
            value={params.sort}
            onChange={event => updateParams({ sort: event.target.value, page: undefined })}
          >
            {CUSTOM_INSTANCE_SORTS.map(sort => <option key={sort} value={sort}>{sort}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-500">Direction
          <select
            className="input mt-1"
            value={params.dir}
            onChange={event => updateParams({ dir: event.target.value, page: undefined })}
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </label>
        <button className="btn-ghost" onClick={resetFilters}>Reset</button>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-xs text-gray-400">
          <input
            type="checkbox"
            className="checkbox"
            checked={params.includeDeleted}
            onChange={event => updateParams({ includeDeleted: event.target.checked, page: undefined })}
          />
          Include deleted tombstones
        </label>
        <span className="text-xs text-gray-600">Filters, sorting and pagination are stored in the URL.</span>
      </div>
    </div>

    {notice && <div className="p-3 rounded border border-neon-500/20 bg-neon-500/10 text-sm text-neon-300">{notice}</div>}
    {instancesQuery.isError && <div className="p-4 rounded border border-red-500/30 bg-red-500/10 text-sm text-red-300">
      {customErrorMessage(instancesQuery.error, 'Custom instances could not be loaded.')}
    </div>}

    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[1120px]">
          <thead className="bg-dark-700/50 text-gray-400 text-xs uppercase">
            <tr>
              <th className="px-4 py-3 text-left">Instance / product</th>
              <th className="px-4 py-3 text-left">Image / Agent</th>
              <th className="px-4 py-3 text-left">State</th>
              <th className="px-4 py-3 text-left">Endpoints</th>
              <th className="px-4 py-3 text-left">Resources</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dark-700">
            {instancesQuery.isLoading && <tr><td colSpan={6} className="p-10"><div className="spinner w-8 h-8 mx-auto" /></td></tr>}
            {!instancesQuery.isLoading && instances.map(instance => <tr key={instance.id} className="hover:bg-dark-700/30 align-top">
              <td className="px-4 py-3 max-w-64">
                <Link className="font-medium text-gray-200 hover:text-neon-300" to={`/admin/custom-instances/${instance.id}${detailSearch}`}>{instance.name}</Link>
                <div className="text-xs text-gray-500 mt-1 truncate" title={instance.containerName}>{instance.containerName}</div>
                <div className={`text-xs mt-1 ${instance.product.missing ? 'text-amber-400' : 'text-gray-500'}`}>
                  {instance.product.displayName || instance.product.key || instance.product.id}{instance.product.missing ? ' · metadata unavailable' : ''}
                </div>
              </td>
              <td className="px-4 py-3 max-w-64">
                <div className={instance.image.missing ? 'text-amber-300' : 'text-gray-300'}>{instance.image.version || instance.image.id}</div>
                <div className={`text-xs mt-1 ${instance.agent.missing ? 'text-amber-400' : 'text-gray-500'}`}>
                  {instance.agent.name || instance.agent.address || instance.agent.id}{instance.agent.missing ? ' · missing' : ''}
                </div>
              </td>
              <td className="px-4 py-3">
                <CustomInstanceStatus status={instance.actualState} />
                <div className="text-xs text-gray-500 mt-2">desired {instance.desiredState}</div>
                {instance.lifecycleOperation && <div className="text-xs text-gray-600 mt-1">{instance.lifecycleOperation.type} · {instance.lifecycleOperation.phase}</div>}
              </td>
              <td className="px-4 py-3">
                {instance.endpoints.length ? <div className="space-y-1">{instance.endpoints.map(endpoint => <div key={`${endpoint.key}:${endpoint.protocol}`} className="text-xs">
                  <div className="text-gray-300 break-all">{endpoint.address || `${endpoint.hostPort}/${endpoint.protocol}`}</div>
                  <div className="text-gray-600">{endpoint.key}: {endpoint.containerPort} → {endpoint.hostPort}/{endpoint.protocol}</div>
                </div>)}</div> : <span className="text-xs text-gray-600">No endpoints</span>}
              </td>
              <td className="px-4 py-3 text-xs">
                <div className="text-gray-300">{instance.resources.cpuCores} CPU · {formatBytes(instance.resources.memoryBytes)}</div>
                <div className="text-gray-600 mt-1">{formatBytes(instance.resources.storageBytes)} storage</div>
              </td>
              <td className="px-4 py-3">
                <div className="flex justify-end items-start gap-2">
                  <CustomInstanceActions instance={instance} compact onAccepted={setNotice} />
                  <Link className="btn-ghost p-2" title="View dedicated page" aria-label={`View ${instance.name}`} to={`/admin/custom-instances/${instance.id}${detailSearch}`}>
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </td>
            </tr>)}
            {!instancesQuery.isLoading && instances.length === 0 && <tr><td colSpan={6} className="p-10">
              <div className="empty-state py-4"><Server className="empty-state-icon" /><div className="empty-state-title">No custom instances found</div><p className="empty-state-text">Adjust the URL-backed filters or create an instance from a product.</p></div>
            </td></tr>}
          </tbody>
        </table>
      </div>
    </div>

    <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500">
      <span>{pagination?.total || 0} instances total</span>
      <div className="flex items-center gap-2">
        <button className="btn-ghost p-2" aria-label="Previous page" disabled={params.page <= 1} onClick={() => updateParams({ page: Math.max(1, params.page - 1) })}><ChevronLeft className="w-4 h-4" /></button>
        <span>{pagination?.page || params.page} / {totalPages}</span>
        <button className="btn-ghost p-2" aria-label="Next page" disabled={(pagination?.page || params.page) >= totalPages} onClick={() => updateParams({ page: params.page + 1 })}><ChevronRight className="w-4 h-4" /></button>
        <select aria-label="Rows per page" className="input w-20" value={params.pageSize} onChange={event => updateParams({ pageSize: Number(event.target.value), page: undefined })}>
          <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
        </select>
      </div>
    </div>
  </div>;
}
